import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
} from "@tanstack/react-query";
import apiClient from "../api-client";
import { shouldRetry, getRetryDelay } from "./retry";
import type {
  BlockedUsersPage,
  ConversationCounts,
  ConversationFilter,
  ConversationListItemDto,
  ConversationListPage,
  ConversationParticipantDto,
  ConversationSummaryDto,
  EditMessageInput,
  MessageReportDto,
  MessageResponseDto,
  MessagesPage,
  ReportMessageInput,
  SendMessageInput,
  UpdateGroupInput,
} from "./dtos/messaging.dto";

export const messagingKeys = {
  all: ["messaging"] as const,
  unreadCount: () => [...messagingKeys.all, "unread-count"] as const,
  conversations: () => [...messagingKeys.all, "conversations"] as const,
  conversationCounts: () =>
    [...messagingKeys.conversations(), "counts"] as const,
  conversationLists: () => [...messagingKeys.conversations(), "list"] as const,
  conversationList: (filter: ConversationFilter) =>
    [...messagingKeys.conversationLists(), filter] as const,
  messages: () => [...messagingKeys.all, "messages"] as const,
  messageList: (conversationId: string) =>
    [...messagingKeys.messages(), conversationId] as const,
  participants: (conversationId: string) =>
    [...messagingKeys.all, "participants", conversationId] as const,
  blocks: () => [...messagingKeys.all, "blocks"] as const,
} as const;

// ============ FEATURE FLAG / UNREAD ============

export interface UnreadCountResult {
  count: number;
  featureEnabled: boolean;
}

export function useMessagingUnreadCount(enabled = true) {
  return useQuery<UnreadCountResult>({
    queryKey: messagingKeys.unreadCount(),
    queryFn: async () => {
      try {
        const response = await apiClient.get<{ count: number }>(
          "/messaging/unread-count",
        );
        return { count: response.data.count, featureEnabled: true };
      } catch (err: unknown) {
        const status =
          typeof err === "object" && err !== null && "status" in err
            ? (err as { status: number }).status
            : 0;
        if (status === 404) {
          return { count: 0, featureEnabled: false };
        }
        throw err;
      }
    },
    retry: shouldRetry,
    retryDelay: getRetryDelay,
    enabled,
    staleTime: 30_000,
  });
}

// ============ CONVERSATIONS ============

export function useConversations(
  filter: ConversationFilter = "all",
  enabled = true,
) {
  return useInfiniteQuery({
    queryKey: messagingKeys.conversationList(filter),
    queryFn: async ({
      pageParam,
    }: {
      pageParam: string | undefined;
    }): Promise<ConversationListPage> => {
      const response = await apiClient.get<ConversationListPage>(
        "/messaging/conversations",
        { params: { limit: 20, cursor: pageParam, filter } },
      );
      return response.data;
    },
    initialPageParam: undefined,
    getNextPageParam: (lastPage) => lastPage.pageInfo.nextCursor,
    retry: shouldRetry,
    retryDelay: getRetryDelay,
    enabled,
  });
}

/** GET /messaging/conversations/counts — conversation count per filter bucket. */
export function useConversationCounts(enabled = true) {
  return useQuery({
    queryKey: messagingKeys.conversationCounts(),
    queryFn: async (): Promise<ConversationCounts> => {
      const response = await apiClient.get<ConversationCounts>(
        "/messaging/conversations/counts",
      );
      return response.data;
    },
    retry: shouldRetry,
    retryDelay: getRetryDelay,
    staleTime: 30_000,
    enabled,
  });
}

export function useStartDirectConversation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (
      targetUserId: string,
    ): Promise<ConversationSummaryDto> => {
      const response = await apiClient.post<ConversationSummaryDto>(
        "/messaging/conversations/direct",
        { targetUserId },
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: messagingKeys.conversations(),
      });
    },
  });
}

export function useCreateGroupConversation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      name: string;
      memberIds: string[];
      avatarUrl?: string | null;
    }): Promise<ConversationSummaryDto> => {
      const response = await apiClient.post<ConversationSummaryDto>(
        "/messaging/conversations/group",
        input,
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: messagingKeys.conversations(),
      });
    },
  });
}

// ============ MESSAGES ============

export function useMessages(
  conversationId: string | undefined,
  enabled = true,
) {
  return useInfiniteQuery({
    queryKey: messagingKeys.messageList(conversationId ?? ""),
    queryFn: async ({
      pageParam,
    }: {
      pageParam: string | undefined;
    }): Promise<MessagesPage> => {
      const response = await apiClient.get<MessagesPage>(
        `/messaging/conversations/${conversationId}/messages`,
        { params: { limit: 30, cursor: pageParam } },
      );
      return response.data;
    },
    initialPageParam: undefined,
    getNextPageParam: (lastPage) => lastPage.pageInfo.nextCursor,
    retry: shouldRetry,
    retryDelay: getRetryDelay,
    enabled: enabled && Boolean(conversationId),
  });
}

export function useConversationParticipants(
  conversationId: string | undefined,
  enabled = true,
) {
  return useQuery({
    queryKey: messagingKeys.participants(conversationId ?? ""),
    queryFn: async (): Promise<ConversationParticipantDto[]> => {
      const response = await apiClient.get<ConversationParticipantDto[]>(
        `/messaging/conversations/${conversationId}/participants`,
      );
      return response.data;
    },
    retry: shouldRetry,
    retryDelay: getRetryDelay,
    enabled: enabled && Boolean(conversationId),
  });
}

export function useMarkConversationRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (conversationId: string): Promise<void> => {
      await apiClient.post(`/messaging/conversations/${conversationId}/read`);
    },
    onSuccess: (_data, conversationId) => {
      queryClient.setQueriesData<InfiniteData<ConversationListPage>>(
        { queryKey: messagingKeys.conversationLists() },
        (data) => {
          if (!data) return data;
          return {
            ...data,
            pages: data.pages.map((page) => ({
              ...page,
              results: page.results.map((c) =>
                c.id === conversationId ? { ...c, unreadCount: 0 } : c,
              ),
            })),
          };
        },
      );
    },
  });
}

export function useSendMessage(conversationId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (
      input: SendMessageInput,
    ): Promise<MessageResponseDto> => {
      const formData = new FormData();
      if (input.body) formData.append("body", input.body);
      if (input.identity) formData.append("identity", input.identity);
      if (input.attachment) formData.append("attachment", input.attachment);
      if (input.replyToMessageId)
        formData.append("replyToMessageId", input.replyToMessageId);
      const response = await apiClient.post<MessageResponseDto>(
        `/messaging/conversations/${conversationId}/messages`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      return response.data;
    },
    onSuccess: (message) => {
      addOrReplaceMessageInCache(queryClient, message);
      bumpConversationInCache(queryClient, message);
    },
  });
}

export function useEditMessage(conversationId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      messageId: string;
      body: EditMessageInput["body"];
    }): Promise<MessageResponseDto> => {
      const response = await apiClient.patch<MessageResponseDto>(
        `/messaging/conversations/${conversationId}/messages/${input.messageId}`,
        { body: input.body },
      );
      return response.data;
    },
    onSuccess: (message) => {
      patchMessageInCache(queryClient, message);
      refreshConversationPreviewIfLatest(queryClient, message);
    },
  });
}

export function useDeleteMessage(conversationId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (messageId: string): Promise<MessageResponseDto> => {
      const response = await apiClient.delete<MessageResponseDto>(
        `/messaging/conversations/${conversationId}/messages/${messageId}`,
      );
      return response.data;
    },
    onSuccess: (message) => {
      patchMessageInCache(queryClient, message);
      refreshConversationPreviewIfLatest(queryClient, message);
    },
  });
}

// ============ GROUPS ============

export function useUpdateGroup(conversationId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (
      input: UpdateGroupInput,
    ): Promise<ConversationSummaryDto> => {
      const response = await apiClient.patch<ConversationSummaryDto>(
        `/messaging/conversations/group/${conversationId}`,
        input,
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: messagingKeys.conversations(),
      });
    },
  });
}

export function useUpdateGroupAvatar(conversationId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (avatar: File): Promise<ConversationSummaryDto> => {
      const formData = new FormData();
      formData.append("avatar", avatar);
      const response = await apiClient.patch<ConversationSummaryDto>(
        `/messaging/conversations/group/${conversationId}/avatar`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: messagingKeys.conversations(),
      });
    },
  });
}

export function useAddGroupParticipant(conversationId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (userId: string): Promise<void> => {
      await apiClient.post(
        `/messaging/conversations/group/${conversationId}/participants`,
        { userId },
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: messagingKeys.conversations(),
      });
      queryClient.invalidateQueries({
        queryKey: messagingKeys.participants(conversationId),
      });
    },
  });
}

export function useRemoveGroupParticipant(conversationId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (targetUserId: string): Promise<void> => {
      await apiClient.delete(
        `/messaging/conversations/group/${conversationId}/participants/${targetUserId}`,
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: messagingKeys.conversations(),
      });
      queryClient.invalidateQueries({
        queryKey: messagingKeys.participants(conversationId),
      });
    },
  });
}

export function useLeaveGroup(conversationId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<void> => {
      await apiClient.post(
        `/messaging/conversations/group/${conversationId}/leave`,
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: messagingKeys.conversations(),
      });
    },
  });
}

// ============ ENTITY CONTACT ============

type EntityKind = "projects" | "portfolios" | "challenges";

function contactEntity(kind: EntityKind, identifier: string, message: string) {
  return apiClient
    .post<ConversationSummaryDto>(`/${kind}/${identifier}/contact`, { message })
    .then((r) => r.data);
}

export function useContactProject() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      identifier,
      message,
    }: {
      identifier: string;
      message: string;
    }) => contactEntity("projects", identifier, message),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: messagingKeys.conversations(),
      });
    },
  });
}

export function useContactPortfolio() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      identifier,
      message,
    }: {
      identifier: string;
      message: string;
    }) => contactEntity("portfolios", identifier, message),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: messagingKeys.conversations(),
      });
    },
  });
}

export function useContactChallenge() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      identifier,
      message,
    }: {
      identifier: string;
      message: string;
    }) => contactEntity("challenges", identifier, message),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: messagingKeys.conversations(),
      });
    },
  });
}

// ============ BLOCKS ============

export function useBlockedUsers(enabled = true) {
  return useInfiniteQuery({
    queryKey: messagingKeys.blocks(),
    queryFn: async ({
      pageParam,
    }: {
      pageParam: string | undefined;
    }): Promise<BlockedUsersPage> => {
      const response = await apiClient.get<BlockedUsersPage>(
        "/messaging/blocks",
        {
          params: { limit: 20, cursor: pageParam },
        },
      );
      return response.data;
    },
    initialPageParam: undefined,
    getNextPageParam: (lastPage) => lastPage.pageInfo.nextCursor,
    retry: shouldRetry,
    retryDelay: getRetryDelay,
    enabled,
  });
}

export function useBlockUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (userId: string): Promise<void> => {
      await apiClient.post("/messaging/blocks", { userId });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: messagingKeys.blocks() });
    },
  });
}

export function useUnblockUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (userId: string): Promise<void> => {
      await apiClient.delete(`/messaging/blocks/${userId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: messagingKeys.blocks() });
    },
  });
}

// ============ REPORTS ============

export function useReportMessage() {
  return useMutation({
    mutationFn: async (input: {
      messageId: string;
      reason: ReportMessageInput["reason"];
      detail?: string;
    }): Promise<MessageReportDto> => {
      const response = await apiClient.post<MessageReportDto>(
        `/messaging/messages/${input.messageId}/report`,
        { reason: input.reason, detail: input.detail },
      );
      return response.data;
    },
  });
}

// ============ CACHE HELPERS ============

export function addOrReplaceMessageInCache(
  queryClient: ReturnType<typeof useQueryClient>,
  message: MessageResponseDto,
) {
  queryClient.setQueryData<InfiniteData<MessagesPage>>(
    messagingKeys.messageList(message.conversationId),
    (data) => {
      if (!data) return data;
      const exists = data.pages.some((p) =>
        p.results.some((m) => m.id === message.id),
      );
      if (exists) {
        return {
          ...data,
          pages: data.pages.map((page) => ({
            ...page,
            results: page.results.map((m) =>
              m.id === message.id ? message : m,
            ),
          })),
        };
      }
      return {
        ...data,
        pages: data.pages.map((page, i) =>
          i === 0 ? { ...page, results: [message, ...page.results] } : page,
        ),
      };
    },
  );
}

export function patchMessageInCache(
  queryClient: ReturnType<typeof useQueryClient>,
  message: MessageResponseDto,
) {
  queryClient.setQueryData<InfiniteData<MessagesPage>>(
    messagingKeys.messageList(message.conversationId),
    (data) => {
      if (!data) return data;
      return {
        ...data,
        pages: data.pages.map((page) => ({
          ...page,
          results: page.results.map((m) => (m.id === message.id ? message : m)),
        })),
      };
    },
  );
}

export function patchReplyPreviewsInCache(
  queryClient: ReturnType<typeof useQueryClient>,
  conversationId: string,
  targetId: string,
  patch: { body?: string | null; deletedAt?: string | null },
) {
  queryClient.setQueryData<InfiniteData<MessagesPage>>(
    messagingKeys.messageList(conversationId),
    (data) => {
      if (!data) return data;
      return {
        ...data,
        pages: data.pages.map((page) => ({
          ...page,
          results: page.results.map((m) =>
            m.replyTo && m.replyTo.id === targetId
              ? { ...m, replyTo: { ...m.replyTo, ...patch } }
              : m,
          ),
        })),
      };
    },
  );
}

export function removeMessageFromCache(
  queryClient: ReturnType<typeof useQueryClient>,
  conversationId: string,
  messageId: string,
) {
  queryClient.setQueryData<InfiniteData<MessagesPage>>(
    messagingKeys.messageList(conversationId),
    (data) => {
      if (!data) return data;
      return {
        ...data,
        pages: data.pages.map((page) => ({
          ...page,
          results: page.results.filter((m) => m.id !== messageId),
        })),
      };
    },
  );
}

export function refreshConversationPreviewIfLatest(
  queryClient: ReturnType<typeof useQueryClient>,
  message: MessageResponseDto,
) {
  queryClient.setQueriesData<InfiniteData<ConversationListPage>>(
    { queryKey: messagingKeys.conversationLists() },
    (data) => {
      if (!data) return data;
      return {
        ...data,
        pages: data.pages.map((page) => ({
          ...page,
          results: page.results.map((c) =>
            c.id === message.conversationId &&
            c.lastMessageAt === message.createdAt
              ? { ...c, lastMessagePreview: previewFromMessage(message) }
              : c,
          ),
        })),
      };
    },
  );
}

export function bumpConversationInCache(
  queryClient: ReturnType<typeof useQueryClient>,
  message: MessageResponseDto,
) {
  queryClient.setQueriesData<InfiniteData<ConversationListPage>>(
    { queryKey: messagingKeys.conversationLists() },
    (data) => {
      if (!data) return data;
      return {
        ...data,
        pages: data.pages.map((page) => ({
          ...page,
          results: page.results.map((c) =>
            c.id === message.conversationId
              ? {
                  ...c,
                  lastMessageAt: message.createdAt,
                  lastMessagePreview: previewFromMessage(message),
                }
              : c,
          ),
        })),
      };
    },
  );
}

export function incrementConversationUnread(
  queryClient: ReturnType<typeof useQueryClient>,
  conversationId: string,
) {
  queryClient.setQueriesData<InfiniteData<ConversationListPage>>(
    { queryKey: messagingKeys.conversationLists() },
    (data) => {
      if (!data) return data;
      return {
        ...data,
        pages: data.pages.map((page) => ({
          ...page,
          results: page.results.map((c) =>
            c.id === conversationId
              ? { ...c, unreadCount: (c.unreadCount ?? 0) + 1 }
              : c,
          ),
        })),
      };
    },
  );
}

export function dropConversationFromCache(
  queryClient: ReturnType<typeof useQueryClient>,
  conversationId: string,
) {
  queryClient.setQueriesData<InfiniteData<ConversationListPage>>(
    { queryKey: messagingKeys.conversationLists() },
    (data) => {
      if (!data) return data;
      return {
        ...data,
        pages: data.pages.map((page) => ({
          ...page,
          results: page.results.filter(
            (c: ConversationListItemDto) => c.id !== conversationId,
          ),
        })),
      };
    },
  );
}

function previewFromMessage(message: MessageResponseDto): string | null {
  if (message.deletedAt) return null;
  if (message.body) return message.body.slice(0, 280);
  if (message.attachment) return `📎 ${message.attachment.name}`;
  return null;
}
